// /assets/cita_events/js/http/utils.js

/* -------------------- Konstanten & State -------------------- */
const CSRF_META_SELECTORS = ['meta[name="csrf-token"]', 'meta[name="csrf_token"]'];
const USER_META_SELECTOR  = 'meta[name="cita-user"]';
const CSRF_ENDPOINT       = '/api/method/cita_events.api.core.registration.get_csrf_token';

const dev = !!window.dev_server;
const GUEST_HEADER_NAME = 'X-Guest-CSRF-Token';
const DEFAULT_ERROR_TOASTS = {
  bad_request: 'Ungültige Anfrage.',
  unauthorized: 'Bitte anmelden.',
  forbidden: 'Zugriff verweigert.',
  not_found: 'Ressource nicht gefunden.',
  internal_error: 'Interner Serverfehler.',
  service_unavailable: 'Dienst aktuell nicht verfügbar.',
  gateway_timeout: 'Zeitüberschreitung.',
  response_contract_violation: 'Ungültiges Antwortformat.',
  two_factor_required: 'Zwei-Faktor-Verifizierung erforderlich.',
  // Welle PRE-PROD-Welle F-9 (2026-05-10): canonical decorator-codes
  // aus @with_resource_context / @with_event_context / @with_organizer_
  // context. Generic-Fallback-Microcopy fuer non-handled Pfade —
  // module-spezifische Microcopy (z.B. checkin-modals.js) hat Vorrang.
  missing_param: 'Pflichtparameter fehlt.',
  // endpoint_removed = HTTP 410 Gone (DSGVO Art.17 — Reg/Resource ist
  // permanent anonymisiert). 8bae480be aktivierte die Defense.
  endpoint_removed: 'Diese Ressource wurde anonymisiert (DSGVO).',
  feature_blocked: 'Diese Funktion ist für deinen Plan nicht verfügbar.',
  // Welle W-SECURITY-QUICK-WINS-BUNDLE-1 Quick-Win A1 (2026-05-26 Frida):
  // HTTP 429 → 'rate_limited'-Code (Z.~321 mapping unten) hatte vorher KEINE
  // Microcopy → toast=null → User sah Search-Box-leer ohne Erklaerung.
  // Welle DRIFT-1 erzwingt zunehmend Rate-Limits — sichtbare 429-UX Pflicht.
  rate_limited: 'Zu viele Anfragen. Bitte einen Moment warten und erneut versuchen.',
};

let guestCsrfToken = '';
let inflightCsrfPromise = null;

/* -------------------- Token-State Helpers -------------------- */
function normaliseGuestToken(token) {
  return typeof token === 'string' ? token.trim() : '';
}
function setGuestCsrfToken(token) {
  const t = normaliseGuestToken(token);
  if (!t) return guestCsrfToken;
  guestCsrfToken = t;
  return guestCsrfToken;
}
export const readGuestCsrfToken = () => guestCsrfToken;

/* -------------------- Sync aus Response -------------------- */
/** Header > Payload > Fallback > alter State (keine Cookies lesen – HttpOnly) */
function syncGuestTokenFromResponse(res, payload, fallback = '') {
  const headerToken  = res?.headers?.get?.(GUEST_HEADER_NAME) || '';
  const payloadToken = extractGuestCsrfToken(payload);
  const chosen = headerToken || payloadToken || fallback || guestCsrfToken;
  if (typeof chosen === 'string' && chosen.length >= 8) setGuestCsrfToken(chosen);
}

/* -------------------- Meta Helpers -------------------- */
function readMetaContent(selector) {
  try {
    const node = document.querySelector(selector);
    const content = node?.getAttribute?.('content') || '';
    return typeof content === 'string' ? content.trim() : '';
  } catch (error) {
    if (dev) console.warn('http.utils.read_meta_failed', selector, error);
    return '';
  }
}

/**
 * Check if a CSRF token value is valid (not a placeholder string).
 * Python `None` renders as string "None" in templates - this must be rejected.
 */
function isValidCsrfToken(value) {
  if (!value || typeof value !== 'string') return false;
  const invalidTokens = ['none', 'null', 'undefined', ''];
  return !invalidTokens.includes(value.toLowerCase());
}

export function readCsrfToken() {
  for (const selector of CSRF_META_SELECTORS) {
    const value = readMetaContent(selector);
    if (isValidCsrfToken(value)) return value;
  }
  const frappeToken = window.frappe?.csrf_token;
  return isValidCsrfToken(frappeToken) ? frappeToken : '';
}

export function writeCsrfToken(token) {
  if (typeof token !== 'string' || !token.trim()) return;
  for (const selector of CSRF_META_SELECTORS) {
    try {
      const node = document.querySelector(selector);
      if (node?.setAttribute) node.setAttribute('content', token);
    } catch (error) {
      if (dev) console.warn('http.utils.write_meta_failed', selector, error);
    }
  }
  // Legacy
  window.frappe = window.frappe || {};
  window.frappe.csrf_token = token;
}

/* -------------------- Wait-Helper -------------------- */
async function waitForGuestCsrfToken({ attempts = 5, delay = 50 } = {}) {
  let token = readGuestCsrfToken();
  if (token) return token;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const ms = typeof delay === 'number' && delay > 0 ? delay * (attempt + 1) : 50;
    await new Promise((resolve) => setTimeout(resolve, ms));
    token = readGuestCsrfToken();
    if (token) break;
  }
  return token || '';
}

export function readCitaUser() {
  const hinted = readMetaContent(USER_META_SELECTOR);
  if (hinted) return hinted;
  if (window.frappe?.boot?.user) return String(window.frappe.boot.user);
  return 'Guest';
}
export const isGuestUser = () => (readCitaUser() || 'Guest').toLowerCase() === 'guest';

/* -------------------- Idempotency -------------------- */
export function createIdempotencyKey() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    if (window.crypto?.getRandomValues) {
      const buf = new Uint8Array(16);
      window.crypto.getRandomValues(buf);
      return Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (e) {
    if (dev) console.warn('http.utils.idempotency_fallback', e);
  }
  return `cita-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

/* -------------------- CSRF Extractors -------------------- */
function extractCsrfToken(payload) {
  if (!payload) return '';
  if (typeof payload === 'string') return payload;
  if (typeof payload === 'object') {
    if (typeof payload.csrf_token === 'string') return payload.csrf_token;
    if (payload.data) {
      const fromData = extractCsrfToken(payload.data);
      if (fromData) return fromData;
    }
    if (payload.message) return extractCsrfToken(payload.message);
  }
  return '';
}

function extractGuestCsrfToken(payload) {
  if (!payload || typeof payload !== 'object') return '';
  if (typeof payload.guest_csrf_token === 'string' && /^[A-Za-z0-9\-_]{8,}$/.test(payload.guest_csrf_token)) {
    return payload.guest_csrf_token;
  }
  if (payload.data && typeof payload.data === 'object') {
    const v = payload.data.guest_csrf_token;
    if (typeof v === 'string' && /^[A-Za-z0-9\-_]{8,}$/.test(v)) return v;
  }
  if (payload.message && typeof payload.message === 'object') {
    const v = payload.message.guest_csrf_token;
    if (typeof v === 'string' && /^[A-Za-z0-9\-_]{8,}$/.test(v)) return v;
    const nestedData = payload.message?.['data'];
    if (nestedData && typeof nestedData === 'object') {
      const nested = nestedData.guest_csrf_token;
      if (typeof nested === 'string' && /^[A-Za-z0-9\-_]{8,}$/.test(nested)) return nested;
    }
  }
  return '';
}

/* -------------------- Fetch CSRF (GET) -------------------- */
async function fetchCsrfToken(endpoint = CSRF_ENDPOINT) {
  try {
    const res = await fetch(endpoint, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const j = await res.json().catch(() => ({}));
    const token = extractCsrfToken(j);
    syncGuestTokenFromResponse(res, j);
    return token;
  } catch {
    return '';
  }
}

/* -------------------- Ensure CSRF in RAM -------------------- */
export async function ensureCsrf(options = {}) {
  const { forceGuest = false } = options;

  const guestSession   = forceGuest ? true : isGuestUser();
  const haveSessionTok = !!readCsrfToken();
  const haveGuestTok   = guestSession ? !!readGuestCsrfToken() : true;

  if (haveSessionTok && haveGuestTok) return readCsrfToken();
  if (inflightCsrfPromise) return inflightCsrfPromise;

  inflightCsrfPromise = (async () => {
    const tok = await fetchCsrfToken();
    if (tok) writeCsrfToken(tok);

    if (guestSession) {
      await waitForGuestCsrfToken({ attempts: 10, delay: 25 });
    }
    return readCsrfToken();
  })();

  try {
    return await inflightCsrfPromise;
  } finally {
    inflightCsrfPromise = null;
  }
}

/* -------------------- Body/Parse Helpers -------------------- */
function isJsonBody(method, body) {
  if (!body) return false;
  if (/^(GET|HEAD)$/i.test(method)) return false;
  if (body instanceof FormData) return false;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (typeof ArrayBuffer !== 'undefined' &&
      (body instanceof ArrayBuffer || ArrayBuffer.isView?.(body))) return false;
  return typeof body === 'object';
}

async function parseMaybeJson(res) {
  if (!res) return {};
  const ct = res.headers?.get?.('content-type') || '';
  try {
    if (ct.includes('application/json')) return await res.json();
    const text = await res.text();
    try { return text ? JSON.parse(text) : {}; } catch { return { message: text }; }
  } catch { return {}; }
}

function isRecord(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function readString(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function firstString(candidates = []) {
  for (const value of candidates) {
    const current = readString(value);
    if (current) return current;
  }
  return '';
}

function parseFrappeServerMessage(value) {
  if (!value) return '';

  if (Array.isArray(value)) {
    for (const item of value) {
      const parsed = parseFrappeServerMessage(item);
      if (parsed) return parsed;
    }
    return '';
  }

  if (isRecord(value)) {
    return firstString([
      value.toast,
      value.message,
      value.error,
      // value.exception / value.exc intentionally NOT read (CWE-209)
    ]);
  }

  const raw = readString(value);
  if (!raw) return '';

  try {
    const parsed = JSON.parse(raw);
    return parseFrappeServerMessage(parsed);
  } catch {
    if (/traceback/i.test(raw)) return '';
    if (raw.length > 280) return '';
    return raw;
  }
}

function resolveEnvelope(payload) {
  if (!isRecord(payload)) {
    return { root: null, envelope: null, source: 'none' };
  }

  const rootEnvelope = toCanonicalEnvelope(payload);
  if (rootEnvelope) {
    return { root: payload, envelope: rootEnvelope, source: 'root' };
  }

  const messageEnvelope = toCanonicalEnvelope(payload.message);
  if (messageEnvelope) {
    return { root: payload, envelope: messageEnvelope, source: 'message' };
  }

  return { root: payload, envelope: null, source: 'invalid' };
}

function mapHttpStatusToCode(status) {
  const mapped = {
    400: 'bad_request',
    401: 'unauthorized',
    403: 'forbidden',
    404: 'not_found',
    405: 'method_not_allowed',
    408: 'request_timeout',
    409: 'conflict',
    410: 'endpoint_removed',
    413: 'payload_too_large',
    415: 'unsupported_media_type',
    422: 'unprocessable_entity',
    429: 'rate_limited',
    500: 'internal_error',
    502: 'bad_gateway',
    503: 'service_unavailable',
    504: 'gateway_timeout',
  };
  if (!Number.isInteger(status)) return '';
  return mapped[status] || `http_${status}`;
}

function extractCodeCandidate(value) {
  const candidate = readString(value).toLowerCase();
  if (!candidate) return '';
  if (!/^[a-z0-9][a-z0-9_.:-]{1,79}$/.test(candidate)) return '';
  return candidate;
}

function toCanonicalEnvelope(candidate) {
  if (!isRecord(candidate)) return null;
  const required = ['success', 'code', 'toast', 'data'];
  if (required.some((key) => !Object.prototype.hasOwnProperty.call(candidate, key))) {
    return null;
  }
  if (typeof candidate.success !== 'boolean') return null;
  const code = extractCodeCandidate(candidate.code);
  if (!code) return null;
  if (candidate.toast !== null && candidate.toast !== undefined && typeof candidate.toast !== 'string') {
    return null;
  }
  if (!isRecord(candidate.data)) return null;
  return {
    success: candidate.success === true,
    code,
    toast: readString(candidate.toast) || null,
    data: candidate.data,
  };
}

function logContractViolation(url, status, payload, envelope) {
  if (!dev) return;
  const rootKeys = isRecord(payload) ? Object.keys(payload).slice(0, 10) : [];
  const envelopeKeys = isRecord(envelope) ? Object.keys(envelope).slice(0, 10) : [];
  console.warn('http.utils.response_contract_violation', {
    url: readString(url) || null,
    status: Number.isInteger(status) ? status : null,
    rootKeys,
    envelopeKeys,
  });
}

export function createTransportMeta(source = null, { status = null, failed = null } = {}) {
  const resolvedStatus = Number.isInteger(status)
    ? status
    : (Number.isInteger(source?.status) ? source.status : null);
  const resolvedFailed = typeof failed === 'boolean'
    ? failed
    : (typeof source?.failed === 'boolean'
        ? source.failed
        : (typeof source?.ok === 'boolean'
            ? !source.ok
            : (resolvedStatus !== null ? resolvedStatus >= 400 : false)));

  return {
    status: resolvedStatus,
    failed: resolvedFailed,
  };
}

/**
 * Kanonischer API-Normalizer (fail-closed).
 * Erfolg ist nur gültig, wenn ein vollständiges kanonisches Envelope vorliegt.
 */
export function normalizeApiResponse(payload, { response = null, transport = null, url = '' } = {}) {
  const { root, envelope, source } = resolveEnvelope(payload);
  const transportMeta = transport
    ? createTransportMeta(transport)
    : createTransportMeta(response);
  const httpFailed = transportMeta.failed;
  const httpStatus = transportMeta.status;

  const serverMessage = firstString([
    parseFrappeServerMessage(root?._server_messages),
    // root.exc / root.exception NOT extracted as user-facing message (CWE-209)
    parseFrappeServerMessage(root?.message?._server_messages),
    // root.message.exc / root.message.exception NOT extracted as user-facing message (CWE-209)
  ]);

  // Hard exceptions: actual Python tracebacks / exc signals — always fatal.
  const hasHardException = Boolean(
    root?.exc ||
    root?.exception ||
    root?.message?.exc ||
    root?.message?.exception
  );

  // Soft signals: _server_messages from frappe.msgprint (info/warning hints).
  // These should NOT override a valid canonical envelope with success: true,
  // because DocType validate() may emit informational msgprints.
  const hasServerMessages = Boolean(
    serverMessage ||
    root?._server_messages ||
    root?.message?._server_messages
  );

  // hasException is true for hard exceptions always, and for soft signals
  // only when there is NO valid canonical success envelope.
  const hasException = hasHardException || (hasServerMessages && !(envelope?.success === true));

  const contractViolation =
    !httpFailed &&
    !hasException &&
    !envelope;

  const success = !httpFailed && !hasHardException && envelope?.success === true;

  let code = envelope?.code || '';
  const legacySuccessCode = code === 'ok' || code === 'success';
  if (hasException && (!code || legacySuccessCode)) {
    code = 'server_exception';
  }
  if (httpFailed && (!code || legacySuccessCode)) {
    code = mapHttpStatusToCode(httpStatus) || 'http_error';
  }
  if (contractViolation && (!code || legacySuccessCode)) {
    code = 'response_contract_violation';
  }
  if (!success && !httpFailed && !hasException && !contractViolation && legacySuccessCode) {
    code = 'request_failed';
  }
  if (!code) {
    if (hasException) code = 'server_exception';
    else if (httpFailed) code = mapHttpStatusToCode(httpStatus) || 'http_error';
    else if (contractViolation) code = 'response_contract_violation';
    else if (success) code = 'success';
    else code = 'request_failed';
  }

  if (contractViolation) {
    logContractViolation(url, httpStatus, payload, envelope || root?.message || root);
  }

  let toast = envelope?.toast || null;
  if (!toast && hasException) {
    toast = serverMessage || null;
  }
  if (!toast && !success) {
    toast = DEFAULT_ERROR_TOASTS[code] || null;
  }

  return {
    success,
    code,
    toast,
    data: envelope?.data || {},
    contractViolation,
    hasException,
    httpStatus,
    envelopeSource: source,
  };
}

/**
 * Extract the canonical error `code` from a thrown frappe.call/jQuery-XHR
 * rejection (TC-04, promoted from tickets-v2/guestlist/index.js).
 *
 * frappe.call() rejects with a jQuery XHR object on HTTP >= 400. The canonical
 * `json_err` envelope rides in `responseJSON` (or, when the content-type is not
 * JSON, in the raw `responseText`). Frappe may also wrap it as
 * `{ message: <envelope> }`, so we look at both the root and `.message`.
 *
 * @param {*} error - jQuery XHR rejection (or any object carrying responseJSON/responseText)
 * @returns {string|null} the backend `code` (e.g. 'duplicate') or null
 */
export function extractErrorCode(error) {
  const json = error?.responseJSON
    || (typeof error?.responseText === 'string'
      ? (() => { try { return JSON.parse(error.responseText); } catch { return null; } })()
      : null);
  if (!json || typeof json !== 'object') return null;
  const envelope = json.message || json;
  return (envelope && typeof envelope === 'object' ? envelope.code : null) || null;
}

/**
 * Single duplicate-outcome normalizer (TC-04).
 *
 * Backend duplicate-guest detection reaches the FE via TWO transports:
 *  - a 200-envelope: frappe.call() RESOLVES, normalizeApiResponse() yields
 *    `{ code: 'duplicate' }` (passed here as the normalized result object).
 *  - a 409-throw: frappe.call() REJECTS with a jQuery XHR; the `json_err`
 *    envelope (code 'duplicate') rides in responseJSON/responseText.
 *
 * Both shapes funnel through this ONE boundary so the two call-sites agree on a
 * single duplicate outcome. The backend `json_err` code ('duplicate') is the
 * canonical contract and is NOT changed here.
 *
 * @param {*} resultOrError - a normalizeApiResponse() result OR a thrown XHR
 * @returns {boolean} true iff this represents the 'duplicate' outcome
 */
export function isDuplicateOutcome(resultOrError) {
  if (!resultOrError || typeof resultOrError !== 'object') return false;
  // 200-envelope: a normalized result carries `.code` directly.
  if (resultOrError.code === 'duplicate') return true;
  // 409-throw: extract the envelope code from the jQuery-XHR rejection.
  return extractErrorCode(resultOrError) === 'duplicate';
}

export function normalizeApiMethodResponse(response, { url = '' } = {}) {
  const body = response?.responseJSON ?? response ?? {};
  return normalizeApiResponse(body, {
    transport: createTransportMeta(response),
    url,
  });
}

function buildCanonicalEnvelope(normalized) {
  const code = readString(normalized?.code) || 'request_failed';
  const toast = readString(normalized?.toast) || null;
  return {
    success: Boolean(normalized?.success),
    code,
    toast,
    data: isRecord(normalized?.data) ? normalized.data : {},
  };
}

function attachCanonicalEnvelope(payload, normalized) {
  const envelope = buildCanonicalEnvelope(normalized);

  if (isRecord(payload)) {
    return { ...envelope };
  }

  return { ...envelope };
}

/* -------------------- Headers Builder -------------------- */
export function buildHeaders({ json = false, extra = {}, method } = {}) {
  const h = new Headers();
  h.set('Accept', 'application/json');
  h.set('X-Requested-With', 'XMLHttpRequest');

  const token = readCsrfToken();
  if (token && token !== 'None' && token.trim() !== '') {
    h.set('X-Frappe-CSRF-Token', token);
  }

  if (shouldAttachGuestHeader(method)) {
    const guestToken = readGuestCsrfToken();
    if (guestToken) h.set(GUEST_HEADER_NAME, guestToken);
  }

  if (json) {
    h.set('Content-Type', 'application/json');
  }

  // nur unkritische Header aus `extra` zulassen
  if (extra && typeof extra === 'object') {
    const blocked = new Set([
      'x-frappe-csrf-token',
      GUEST_HEADER_NAME.toLowerCase(),
      'x-requested-with',
      'accept',
      'content-type',
    ]);
    for (const [k, v] of Object.entries(extra)) {
      if (v == null) continue;
      const key = String(k).toLowerCase();
      if (blocked.has(key)) continue;
      h.set(k, v);
    }
  }
  return Object.fromEntries(h.entries());
}

/** Mutierend? Dann muss der Guest-Header dran – egal ob wir den Gaststatus korrekt erkennen oder nicht. */
function shouldAttachGuestHeader(method) {
  if (/^(GET|HEAD)$/i.test(method)) return false;
  if (readGuestCsrfToken()) return true;
  return isGuestUser();
}

/* -------------------- Robust Fetch mit CSRF -------------------- */
export async function fetchJson(url, { method = 'GET', body, headers = {}, retryOnCsrf = true, ...rest } = {}) {
  const m = method.toUpperCase();
  const json = isJsonBody(m, body);

  // Preflight (robust):
  // - bei Mutationen: IMMER Guest-Token & Session-Token vorbereiten
  // - bei GET: falls kein Session-CSRF vorhanden ist, ebenfalls vorbereiten (schadet nicht)
  if (!/^(GET|HEAD)$/i.test(m)) {
    await ensureCsrf({ forceGuest: true });
  } else if (!readCsrfToken()) {
    await ensureCsrf({ forceGuest: true });
  }

  let hdrs = buildHeaders({ json, extra: headers, method: m });

  // Für GET/HEAD keine CSRF-Header mitschicken (spart Noise, ist optional)
  if (m === 'GET' || m === 'HEAD') {
    delete hdrs['X-Frappe-CSRF-Token'];
    delete hdrs[GUEST_HEADER_NAME];
  }

  // Client-side timeout to prevent infinite hangs (e.g. server-side backoff/lock waits).
  // Callers can override via rest.signal if they need a custom AbortController.
  const DEFAULT_TIMEOUT_MS = 45000;
  const externalSignal = rest.signal;
  delete rest.signal;

  const doFetch = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

    // If caller passed a signal, abort our controller when theirs aborts
    if (externalSignal) {
      if (externalSignal.aborted) { controller.abort(); }
      else { externalSignal.addEventListener('abort', () => controller.abort(), { once: true }); }
    }

    let res;
    try {
      res = await fetch(url, {
        method: m,
        credentials: 'include',
        headers: hdrs,
        body: json ? JSON.stringify(body) : body,
        signal: controller.signal,
        ...rest,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    const rtok = res.headers?.get?.('X-Frappe-CSRF-Token') || res.headers?.get?.('x-frappe-csrf-token');
    if (rtok) writeCsrfToken(rtok);

    const payload = await parseMaybeJson(res);
    syncGuestTokenFromResponse(res, payload);
    return { res, payload };
  };

  let { res, payload } = await doFetch();

  const hasBodyEvidence = !!payload && (
    payload.exc_type === 'CSRFTokenError' ||
    String(payload.exception || '').includes('CSRFTokenError') ||
    (String(payload?.error?.code || '').toUpperCase() === 'PERMISSION_DENIED' &&
     /csrf/i.test(String(payload?.error?.message || '')))
  );
  const looksLikeCsrf =
    (!!res && res.status === 417) ||
    (!!res && res.status === 403 && hasBodyEvidence) ||
    hasBodyEvidence;

  if (!/^(GET|HEAD)$/i.test(m) && retryOnCsrf && looksLikeCsrf) {
    // Gleiche Strategie beim Retry: Tokens frisch sicherstellen
    await ensureCsrf({ forceGuest: true });
    hdrs = buildHeaders({ json, extra: headers, method: m });
    ({ res, payload } = await doFetch());
  }

  const normalized = normalizeApiResponse(payload, { response: res, url });
  const canonicalPayload = attachCanonicalEnvelope(payload, normalized);
  return { res, payload: canonicalPayload, raw: payload, normalized };
}

/* -------------------- Bequeme Wrapper -------------------- */
export async function callMethod(methodName, args = {}, opts = {}) {
  return fetchJson(`/api/method/${methodName}`, {
    method: 'POST',
    body: args,
    headers: { 'X-Idempotency-Key': createIdempotencyKey() },
    ...opts,
  });
}

export async function ensureGuestCsrfReady(options) {
  const csrfToken = await ensureCsrf({ mutating: false, forceGuest: true });
  let guestToken = readGuestCsrfToken();
  if (!guestToken) {
    guestToken = await waitForGuestCsrfToken(options);
  }
  return { csrfToken, guestToken };
}

/* -------------------- Dev-Debug -------------------- */
if (dev) {
  window.__csrf_debug = {
    get user()  { return readCitaUser(); },
    get csrf()  { return readCsrfToken(); },
    get guest() { return readGuestCsrfToken(); },
  };
}

export default {
  readCsrfToken,
  writeCsrfToken,
  readCitaUser,
  isGuestUser,
  createIdempotencyKey,
  buildHeaders,
  fetchJson,
  ensureCsrf,
  ensureGuestCsrfReady,
  readGuestCsrfToken,
  callMethod,
  createTransportMeta,
  normalizeApiMethodResponse,
  normalizeApiResponse,
  extractErrorCode,
  isDuplicateOutcome,
};
